import {
  isFocusableTarget,
  nearestFocusableAncestor
} from 'v5/platform/dom/isFocusable';

const ENTITY_LIST_SELECTION_AFFORDANCE_SELECTOR =
  '[data-entity-list-selection-affordance], [data-entity-grid-selection-toggle], [data-selection-affordance]';

const COLLECTION_ROOT_SELECTOR =
  '[role="grid"], [role="treegrid"], [role="listbox"]';

/** Opt out of marquee start (e.g. table sticky header when SelectionLayout wraps the container). */
const ENTITY_LIST_NO_MARQUEE_SELECTOR = '[data-entity-list-no-marquee]';

/**
 * `true` when `target` is inside a control that should not drive marquee selection.
 *
 * Selection affordances (row checkboxes, grid selection toggles) are excluded so
 * marquee select can start from the selection column; a plain click still toggles
 * via the affordance's own handler.
 *
 * {@link ENTITY_LIST_NO_MARQUEE_SELECTOR} wins over affordances (whole header regions).
 *
 * Uses {@link isFocusableTarget} plus `[data-entity-grid-tile-interactive]` for non-DOM
 * focusable wrappers (e.g. row action regions).
 *
 * React Aria `GridListItem` rows are often focusable (`tabindex`) for keyboard
 * navigation; clicks on that row shell must not count as interactive, or marquee
 * selection and grid selection-suppression break.
 */
export function isPointerEventFromTileInteractiveControl(
  target: EventTarget | null,
  {
    collectionRootIsShell = false
  }: {
    /**
     * A React Aria collection is the tab stop itself until one of its items takes focus: a press
     * on it then is on its shell too, not on a control.
     */
    collectionRootIsShell?: boolean;
  } = {}
): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest(ENTITY_LIST_NO_MARQUEE_SELECTOR)) return true;
  if (target.closest(ENTITY_LIST_SELECTION_AFFORDANCE_SELECTOR)) return false;
  if (target.closest('[data-entity-grid-tile-interactive]')) {
    return true;
  }
  const tileItemRoot = target.closest('[data-item-id]');
  const nearestFocusable = nearestFocusableAncestor(target);
  if (
    tileItemRoot instanceof Element &&
    nearestFocusable !== null &&
    nearestFocusable === tileItemRoot
  ) {
    return false;
  }
  if (
    collectionRootIsShell &&
    nearestFocusable?.matches(COLLECTION_ROOT_SELECTOR)
  ) {
    return false;
  }
  return isFocusableTarget(target);
}
